import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Check, Copy, FileText } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchInput } from "@/components/ui/search-input";

// Eagerly bundles every markdown file under docs/ as raw text at build time.
const docModules = import.meta.glob("/docs/**/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

interface DocEntry {
  path: string; // e.g. "architecture/overview.md"
  folder: string; // e.g. "architecture"
  name: string; // e.g. "overview.md"
  content: string;
}

const docs: DocEntry[] = Object.entries(docModules)
  .map(([fullPath, content]) => {
    const path = fullPath.replace(/^\/docs\//, "");
    const segments = path.split("/");
    const name = segments.pop() as string;
    const folder = segments.length > 0 ? segments.join("/") : "root";
    return { path, folder, name, content };
  })
  .sort((a, b) => a.path.localeCompare(b.path));

const folders = Array.from(new Set(docs.map((d) => d.folder))).sort((a, b) =>
  a === "root" ? -1 : b === "root" ? 1 : a.localeCompare(b),
);

const formatTitle = (name: string) =>
  name
    .replace(/\.md$/, "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const DevDocs = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedPath, setSelectedPath] = useState<string | null>(docs[0]?.path ?? null);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);

  const filteredDocs = useMemo(() => {
    if (!query.trim()) return docs;
    const q = query.toLowerCase();
    return docs.filter((d) => d.path.toLowerCase().includes(q));
  }, [query]);

  const groupedDocs = useMemo(() => {
    const groups = new Map<string, DocEntry[]>();
    for (const doc of filteredDocs) {
      const list = groups.get(doc.folder) ?? [];
      list.push(doc);
      groups.set(doc.folder, list);
    }
    return groups;
  }, [filteredDocs]);

  const selectedDoc = docs.find((d) => d.path === selectedPath) ?? null;

  const handleCopy = async (doc: DocEntry) => {
    await navigator.clipboard.writeText(doc.content);
    setCopiedPath(doc.path);
    setTimeout(() => setCopiedPath((current) => (current === doc.path ? null : current)), 1500);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <PageHeader title="Developer Docs" genieIcon="code" />
        <div className="mb-6">
          <button
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-genie-primary/10 text-genie-primary hover:bg-genie-primary/20 transition-colors"
            onClick={() => navigate("/admin")}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Admin
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
          {/* Sidebar: doc list */}
          <div className="rounded-xl border-2 border-gray-200 bg-white overflow-hidden flex flex-col h-[calc(100vh-220px)] min-h-[400px]">
            <div className="p-3 border-b border-gray-100">
              <SearchInput value={query} onChange={setQuery} placeholder="Search docs..." />
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2">
                {folders
                  .filter((folder) => groupedDocs.has(folder))
                  .map((folder) => (
                    <div key={folder} className="mb-2">
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        {folder === "root" ? "General" : folder}
                      </div>
                      {groupedDocs.get(folder)!.map((doc) => (
                        <button
                          key={doc.path}
                          onClick={() => setSelectedPath(doc.path)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-left transition-colors ${
                            selectedPath === doc.path
                              ? "bg-genie-primary/10 text-genie-primary font-medium"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <FileText className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                          <span className="truncate">{formatTitle(doc.name)}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                {filteredDocs.length === 0 && (
                  <div className="px-2 py-6 text-center text-sm text-gray-400">No docs match "{query}"</div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Content */}
          <div className="rounded-xl border-2 border-gray-200 bg-white overflow-hidden">
            {selectedDoc ? (
              <ScrollArea className="h-[calc(100vh-220px)] min-h-[400px]">
                <div className="px-6 sm:px-10 py-7 max-w-4xl">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="text-xs text-gray-400 font-mono truncate">{selectedDoc.path}</div>
                    <button
                      onClick={() => handleCopy(selectedDoc)}
                      className="inline-flex items-center gap-1.5 shrink-0 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-genie-primary transition-colors"
                    >
                      {copiedPath === selectedDoc.path ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          Copy markdown
                        </>
                      )}
                    </button>
                  </div>
                  <div
                    className="prose prose-sm prose-gray max-w-none
                      prose-headings:font-bold prose-headings:text-genie-text
                      prose-a:text-genie-primary prose-a:no-underline hover:prose-a:underline
                      prose-code:text-genie-primary prose-code:bg-genie-primary/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-code:font-mono prose-code:text-xs
                      prose-pre:bg-gray-900 prose-pre:text-gray-100
                      prose-table:text-xs"
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedDoc.content}</ReactMarkdown>
                  </div>
                </div>
              </ScrollArea>
            ) : (
              <div className="flex items-center justify-center h-[calc(100vh-220px)] min-h-[400px] text-sm text-gray-400">
                Select a document to view it
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevDocs;
